/**
 * 🗄️ SERVICE DE CACHE FRONTEND POUR LES FILTRES
 * 
 * Fonctionnalités:
 * ✅ Cache intelligent avec TTL
 * ✅ Invalidation automatique
 * ✅ Support pour tous types de filtres
 * ✅ Optimisation des appels API
 */

class FilterCacheService {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = {
      countries: 30 * 60 * 1000,      // 30 minutes
      cities: 15 * 60 * 1000,         // 15 minutes
      categories: 60 * 60 * 1000,     // 1 heure (pour futur)
      priceRanges: 10 * 60 * 1000,    // 10 minutes (pour futur)
      amenities: 20 * 60 * 1000       // 20 minutes (pour futur)
    };
  }

  /**
   * 🔑 Générer une clé de cache
   */
  generateKey(type, params = {}) {
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    
    return paramString ? `${type}:${paramString}` : type;
  }

  /**
   * 📥 Récupérer depuis le cache
   */
  get(type, params = {}) {
    const key = this.generateKey(type, params);
    const cached = this.cache.get(key);
    
    if (!cached) {
      console.log(`🔍 [FilterCache] Cache miss for: ${key}`);
      return null;
    }
    
    // Vérifier si expiré
    if (Date.now() > cached.expiry) {
      console.log(`⏰ [FilterCache] Cache expired for: ${key}`);
      this.cache.delete(key);
      return null;
    }
    
    console.log(`✅ [FilterCache] Cache hit for: ${key}`);
    return cached.data;
  }

  /**
   * 💾 Stocker dans le cache
   */
  set(type, data, params = {}, customTTL = null) {
    const key = this.generateKey(type, params);
    const ttl = customTTL || this.defaultTTL[type] || this.defaultTTL.countries;
    const expiry = Date.now() + ttl;
    
    this.cache.set(key, {
      data,
      expiry,
      timestamp: Date.now()
    });
    
    console.log(`💾 [FilterCache] Cached: ${key} (TTL: ${ttl/1000}s)`);
  }

  /**
   * 🗑️ Invalider le cache
   */
  invalidate(type, params = {}) {
    if (params && Object.keys(params).length > 0) {
      // Invalider une clé spécifique
      const key = this.generateKey(type, params);
      this.cache.delete(key);
      console.log(`🗑️ [FilterCache] Invalidated: ${key}`);
    } else {
      // Invalider tout le type
      const keysToDelete = [];
      for (const key of this.cache.keys()) {
        if (key.startsWith(`${type}:`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.cache.delete(key));
      console.log(`🗑️ [FilterCache] Invalidated all ${type} cache (${keysToDelete.length} items)`);
    }
  }

  /**
   * 🧹 Nettoyer le cache expiré
   */
  cleanup() {
    const now = Date.now();
    const keysToDelete = [];
    
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiry) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
    
    if (keysToDelete.length > 0) {
      console.log(`🧹 [FilterCache] Cleaned up ${keysToDelete.length} expired items`);
    }
  }

  /**
   * 📊 Statistiques du cache
   */
  getStats() {
    const now = Date.now();
    let validItems = 0;
    let expiredItems = 0;
    
    for (const value of this.cache.values()) {
      if (now > value.expiry) {
        expiredItems++;
      } else {
        validItems++;
      }
    }
    
    return {
      totalItems: this.cache.size,
      validItems,
      expiredItems,
      memoryUsage: this.cache.size * 100 // Estimation approximative
    };
  }

  /**
   * � Invalider automatiquement après changements de données
   */
  invalidateRelatedCaches(changedData) {
    console.log('🔄 [FilterCache] Invalidation intelligente:', changedData);
    
    if (changedData.country) {
      // Si le pays change, invalider les pays et villes de ce pays
      this.invalidate('countries');
      this.invalidate('cities', { country: changedData.country });
      console.log(`🗑️ Invalidated caches for country: ${changedData.country}`);
    }
    
    if (changedData.city && changedData.country) {
      // Si une ville change, invalider les villes de ce pays
      this.invalidate('cities', { country: changedData.country });
      console.log(`🗑️ Invalidated cities cache for: ${changedData.country}`);
    }
    
    if (changedData.category) {
      // Pour les futures catégories
      this.invalidate('categories');
    }
    
    // Invalider les statistiques globales
    this.invalidate('priceRanges');
  }

  /**
   * 📡 Hook pour écouter les changements de données
   */
  setupDataChangeListener() {
    // Écouter les événements de changement depuis le backend
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('listingChanged', (event) => {
        this.invalidateRelatedCaches(event.detail);
      });
      
      window.addEventListener('listingCreated', (event) => {
        this.invalidateRelatedCaches(event.detail);
      });
      
      window.addEventListener('listingDeleted', (event) => {
        this.invalidateRelatedCaches(event.detail);
      });
    }
  }

  /**
   * �🚀 Précharger les données communes
   */
  async preloadCommonData() {
    console.log('🚀 [FilterCache] Preloading common filter data...');
    
    try {
      // Précharger les pays si pas en cache
      if (!this.get('countries')) {
        const response = await fetch('/api/v1/search/locations/countries');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.countries) {
            this.set('countries', data.countries);
          }
        }
      }
      
      console.log('✅ [FilterCache] Common data preloaded');
    } catch (error) {
      console.error('❌ [FilterCache] Preload failed:', error);
    }
  }
}

// Instance singleton
const filterCacheService = new FilterCacheService();

// Configurer l'écoute des changements de données
filterCacheService.setupDataChangeListener();

// Nettoyer automatiquement toutes les 5 minutes
setInterval(() => {
  filterCacheService.cleanup();
}, 5 * 60 * 1000);

export default filterCacheService;
